import type { ReactNode } from "react";
import { Card } from "@components/ui/card";

/**
 * Workspace — THE canonical page layout: chat on the left, the artifacts
 * panel on the right, footer below. The artifacts panel hosts whatever
 * context the user flips through — website preview, ad preview + edit
 * forms, analytics… Every surface adopts this shell; none re-implements
 * the grid.
 *
 * Extracted verbatim from the website builder (BuildStep), which defined
 * the look: full-height column, `minmax(360px,640px)` chat column, tight
 * 12px gutters, artifacts bleeding behind the full-bleed footer.
 */
export function Workspace({
  chat,
  artifacts,
  footer,
  banner,
}: {
  /** The left column — a WorkspaceChatCard (or a component rendering one). */
  chat: ReactNode;
  /** The right column — preview, forms, whatever the surface flips through. */
  artifacts: ReactNode;
  /** Rendered below the grid; pair with PaginationFooter layout="full-bleed". */
  footer?: ReactNode;
  /** Page-level notifications/modals rendered outside the grid. */
  banner?: ReactNode;
}) {
  return (
    <div className="h-full flex flex-col">
      {banner}
      {/* Main content area - no bottom padding so artifacts extend behind footer */}
      <main className="flex-1 min-h-0 grid grid-cols-[minmax(360px,640px)_1fr] gap-x-3 px-3 pt-3">
        <div className="min-h-0 overflow-hidden">{chat}</div>
        {/* Negative margin extends behind footer, overflow clips rounded corners */}
        <div className="min-h-0 -mb-20 overflow-hidden flex flex-col gap-2">
          {artifacts}
        </div>
      </main>
      {footer}
    </div>
  );
}

/**
 * The chat card shell — the website sidebar's exact Card. Children are the
 * card's stack: optional WorkflowBuddy + Separator, then the surface's chat.
 */
export function WorkspaceChatCard({ children }: { children: ReactNode }) {
  return (
    <Card className="shadow-[0px_0px_8px_4px_rgba(167,165,161,0.08)] bg-background border-neutral-300 rounded-2xl overflow-hidden z-0 py-0 gap-0 w-full flex flex-col min-h-0 h-full">
      {children}
    </Card>
  );
}
